import React, { useRef } from 'react';
import { Button, Modal } from 'antd';
import { Form } from 'formik-antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import FormikFileUpload, {
  BaseFileYupObject,
} from 'components/formik/FormikFileUpload';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import { getFileUploadMessages } from 'utils/fileUtils';
import SubmitButton from 'components/formik/SubmitButton';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { uploadACHPayments } from 'redux/reducers/achPaymentsSlice';
import { FileValue } from 'components/wrappers/BaseFileUpload';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import adminApiClient, { ACHPaymentFileRequestModel } from 'api/adminApiClient';
import BaseDraggerContent from 'components/wrappers/BaseFileUpload/BaseDraggerContent';
import UploadList from 'components/wrappers/BaseFileUpload/UploadList';
import { useAppDispatch } from 'hooks/useAppDispatch';

const FILE_TYPES = ['TXT'];
const fileUploadMessages = getFileUploadMessages({
  fileTypes: FILE_TYPES,
});

const uploadFilesLinkFunction =
  adminApiClient.getACHPaymentFileUploadLink.bind(adminApiClient);

const validationSchema = Yup.object({
  achPayments: Yup.array().of(BaseFileYupObject).min(1, 'Field is required'),
});

interface ACHPaymentsFormValues {
  achPayments: FileValue[];
}

type Props = {
  onClose: () => void;
  onPaymentsUploaded: () => Promise<void>;
};

export default function ACHPaymentsUploaderModal(props: Props) {
  const { onClose, onPaymentsUploaded } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const initialValues: ACHPaymentsFormValues = {
    achPayments: [],
  };

  const handleSubmit = async (values: ACHPaymentsFormValues) => {
    const { achPayments } = values;

    const fileInfos: ACHPaymentFileRequestModel[] = achPayments.map(
      (achPayment) => ({
        fileKey: achPayment.s3Key,
        fileName: achPayment.fileName,
      }),
    );

    const result = await dispatch(uploadACHPayments({ fileInfos }));

    if (uploadACHPayments.fulfilled.match(result)) {
      BaseAlert.success('Files successfully uploaded.');

      await onPaymentsUploaded();
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title="Upload payments"
      open={true}
      onCancel={handleCloseModal}
      maxWidth={468}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <div className="modal-title">Upload files</div>
            <div className="upload-wrapper">
              <FormItem
                name="achPayments"
                label="ACH payments *"
                showValidateError={false}
                data-id="uploadAchPaymentsField"
              >
                <FormikFileUpload
                  name="achPayments"
                  isDraggable
                  includeFileName
                  maxFileCount={50}
                  acceptedFileSize={10}
                  acceptedFileFormat={['text/plain']}
                  getUploadList={UploadList}
                  messages={fileUploadMessages}
                  uploadLinkFunction={uploadFilesLinkFunction}
                  fileNameLength={120}
                >
                  {() => <BaseDraggerContent fileTypes={FILE_TYPES} />}
                </FormikFileUpload>
              </FormItem>
            </div>
          </div>

          <BaseModalFooter>
            <div>
              <Button onClick={handleCloseModal}>Cancel</Button>

              <SubmitButton type="primary" data-id="saveACHPaymentsBtn">
                Save
              </SubmitButton>
            </div>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
